/**
 * Mirror of the current `useDocSync` state so side panels (FilesPanel) can
 * render folder-sync UI without prop drilling. ChatPage owns the hook and
 * pushes updates here; panels subscribe read-only.
 */

import { create } from "zustand";

export interface FolderSyncStore {
  docConnected: boolean;
  /** Session the folder handle is currently bound to, or null. Lets
   *  non-chat callers (session-delete mutation) tear the sync down before
   *  wiping a workspace, without reaching into ChatPage's hook. */
  connectedSessionId: string | null;
  dirHandleName: string | null;
  docFiles: string[];
  uploadedPaths: Set<string>;
  pickerSupported: boolean;
  /**
   * Set when a per-session handle was restored from IndexedDB but the browser
   * needs a user gesture to re-grant readwrite permission. UI shows a
   * "Reconnect «name»" button that calls `regrantPermission`.
   */
  pendingHandleName: string | null;
  /**
   * Name of a handle we tried to restore but couldn't (folder deleted,
   * permission wiped, disk unmounted). Set once when the restore fails, shown
   * as a one-shot warning in FilesPanel. Cleared on next pick/connect.
   */
  staleHandleName: string | null;
  pickFolder: () => Promise<void>;
  disconnect: () => void;
  finishConnect: (
    handle: FileSystemDirectoryHandle,
    mode: "local" | "server" | "merge",
  ) => Promise<void>;
  regrantPermission: () => Promise<void>;
  set: (patch: Partial<Omit<FolderSyncStore, "set">>) => void;
}

export const useFolderSyncStore = create<FolderSyncStore>((set) => ({
  docConnected: false,
  connectedSessionId: null,
  dirHandleName: null,
  docFiles: [],
  uploadedPaths: new Set(),
  pickerSupported: typeof window !== "undefined" && "showDirectoryPicker" in window,
  pendingHandleName: null,
  staleHandleName: null,
  pickFolder: async () => {},
  disconnect: () => {},
  finishConnect: async () => {},
  regrantPermission: async () => {},
  set: (patch) => set(patch),
}));
